import { useEffect, useState, type ReactNode } from 'react';
import { Button, Callout, Classes } from '@blueprintjs/core';
import { Link } from 'react-router-dom';
import { useAppModel } from '@/models/AppModel';
import { css } from '../../../styled-system/css';

const loadingStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  padding: '24px',
  minHeight: '100%',
  background: '#1c2127',
  color: '#f0f3f6',
});

/** Discovery never consumes WebGL palette resources. Prepare them at editor entry. */
export function EditorResourcesBoundary({
  children,
  prepareModule,
}: {
  children: ReactNode;
  prepareModule?: () => Promise<unknown>;
}) {
  const app = useAppModel();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ ready: boolean; error?: string }>({
    ready: false,
  });
  useEffect(() => {
    let active = true;
    Promise.all([app.init(), prepareModule?.()]).then(
      () => {
        if (active) setState({ ready: true });
      },
      (error) => {
        if (active)
          setState({
            ready: false,
            error:
              error instanceof Error
                ? error.message
                : 'The editor graphics could not be prepared.',
          });
      },
    );
    return () => {
      active = false;
    };
  }, [app, attempt, prepareModule]);
  if (state.ready) return children;
  return (
    <main className={`${Classes.DARK} ${loadingStyle}`} aria-label="Opening editor">
      {state.error ? (
        <>
          <Callout intent="danger" role="alert">
            {state.error}
          </Callout>
          <Button
            onClick={() => {
              setState({ ready: false });
              setAttempt((value) => value + 1);
            }}
          >
            Try again
          </Button>
          <Link to="/">Return to Projects</Link>
        </>
      ) : (
        <p role="status">Preparing editor graphics…</p>
      )}
    </main>
  );
}
